import styles from "./index.module.css";
import "./index.css";

import React from "react";
import BaseComponent, {executeComponentCallback} from 'Core/components/BaseComponent';
import PropTypes from "prop-types";
import FormWrapper, {FormField} from 'Core/components/advanced/FormWrapper';
import SelectInput from 'Core/components/input/SelectInput';
import NumberInput from 'Core/components/input/NumberInput';
import {RELATIVE_DATE_TYPE, RELATIVE_DATE_TYPES, RELATIVE_DATE_UNIT, RELATIVE_DATE_UNITS} from './const';
import {capitalize, trimString} from 'Core/helpers/string';
import {getArray, getNumber, getString} from 'Core/helpers/data';
import Button, {BUTTON_STYLE} from 'Core/components/display/Button';
import {formatNumber, getAppNumberLocaleCode} from 'Core/helpers/number';
import {tag_prefix, tag_suffix} from 'Config/app';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import {cloneDeep} from 'lodash';

const initialState = {
	/**
	 * Type of relative date
	 * @type {RelativeDateType}
	 */
	type: RELATIVE_DATE_TYPE.TODAY,
	/**
	 * Unit used to calculate relative date
	 * @type {?RelativeDateUnit}
	 */
	unit: RELATIVE_DATE_UNIT.DAYS,
	/**
	 * Value used to calculate relative date
	 * @type {?number}
	 */
	value: null,
};

class RelativeDateSection extends BaseComponent {
	valueInputRef = null;
	
	constructor(props) {
		super(props, {
			translationPath: 'RelativeDateSection',
			domPrefix: 'relative-date-section',
			
			// Custom component options
			valuePrefix: tag_prefix,
			valueSuffix: tag_suffix,
		});

		this.state = cloneDeep(initialState);
		
		this.getDataToReturn = this.getDataToReturn.bind(this);
	}
	
	componentDidMount() {
		super.componentDidMount();
		
		if (this.valueInputRef) this.valueInputRef.inputRef.focus();
	}
	
	async asyncComponentDidMount() {
		await super.asyncComponentDidMount();
		
		await this.setState(this.getDataToLoad());
	}

	/**
	 * Parse tag data to load into component state values
	 * @return {{type: RelativeDateType, unit: (RelativeDateUnit|null), value: (number|null)}}
	 */
	getDataToLoad() {
		const tag = trimString(getString(this.props, 'data'), '##');
		
		if (!tag) return cloneDeep(initialState);

		const tagParts = tag.split(/([+-])/);
		const type = (tagParts[0] || initialState.type);
		let value = initialState.value;
		let unit = initialState.unit;
		if (tagParts.length > 2) {
			const sign = tagParts[1];
			const remainder = tagParts.slice(2).join('');

			const match = remainder.match(/^(\d+)([a-zA-Z]+)$/);
			if (match) {
				const numValue = parseInt(match[1], 10);
				value = sign === '-' ? -numValue : numValue;
				unit = match[2].toUpperCase();
			}
		}

		return {type, unit, value};
	}

	/**
	 * Get data that will be returned to the parent component
	 * @return {string}
	 */
	getDataToReturn() {
		const {type, value, unit} = this.state;
		
		let dataToReturn = `${this.getOption('valuePrefix')}${type}`;
		if (!!value) dataToReturn += `${formatNumber(value, '+0,0', getAppNumberLocaleCode())}${unit}`;
		return `${dataToReturn}${this.getOption('valueSuffix')}`;
	}
	
	render() {
		const {type, value, unit} = this.state;
		const dateTypes = getArray(this.props, 'dateTypes');
		
		return (
			<div className={`${this.getOption('domPrefix')} ${styles['wrapper']}`}>
				<FormWrapper className="dialog-form">
					<FormField labelPosition={FORM_FIELD_LABEL_POSITION.NONE}>
						<div className={`input-wrapper ${styles['inputWrapper']}`}>
							<SelectInput
								className="date-type-input"
								options={dateTypes.map(i => ({value: i, label: this.tt(i, 'dateType')}))}
								value={type}
								onChange={v => this.setState({type: v})}
							/>
							<NumberInput
								className={`value-input ${styles['valueInput']}`}
								intOnly={true}
								value={value}
								onChange={v => this.setState({value: getNumber(v, '', null)})}
								onEnterKey={() => executeComponentCallback(this.props.onInsert, this.getDataToReturn())}
								placeholder={this.t('valuePlaceholder')}
								ref={node => { this.valueInputRef = node; }}
							/>
							<SelectInput
								className={`unit-input ${styles['unitInput']}`}
								options={RELATIVE_DATE_UNITS.map(i => ({
									value: i, 
									label: this.t(capitalize(i.toLowerCase()), 'time')
								}))}
								value={unit}
								onChange={v => this.setState({unit: v})}
							/>
							<Button
								className={`insert-button ${styles['insertButton']}`}
								icon="arrow-right"
								displayStyle={BUTTON_STYLE.ACTION}
								onClick={() => executeComponentCallback(this.props.onInsert, this.getDataToReturn())}
							/>
						</div>
					</FormField>
				</FormWrapper>
			</div>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
RelativeDateSection.propTypes = {
	dateTypes: PropTypes.arrayOf(PropTypes.oneOf(RELATIVE_DATE_TYPES)),
	
	data: PropTypes.string,
	
	onInsert: PropTypes.func,
};

/**
 * Define component default values for own props
 */
RelativeDateSection.defaultProps = {
	dateTypes: RELATIVE_DATE_TYPES,
};

export default RelativeDateSection;